/**
 * @file RecorderAgendaPill.tsx
 * @brief The recorder dock's "Agenda 2/5" pill and its check-box list (B49).
 *
 * @details
 * DES 02-11-2026: tick agenda items off during the meeting. The dock is a
 * locked design, so this is the one small addition: a pill with the count on
 * the dock's top edge (outside the row, which stays as it was), opening the
 * list above it. Tap an item = done (at this second of the
 * recording), tap again = undo. Frozen (`locked`) once the recording stops.
 * Sub-points are indented under their point, as in the agenda panel.
 *
 * @references agendaChecks.ts, RecorderDock.tsx, docs/product/agenda-checks-b49.md
 *
 * @author DES
 * @date 02-11-2026
 */

import { Check } from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { cn } from "@/shared/theme/cn";
import { isRTLText } from "@/shared/utils/formatters";
import { agendaCheckCounts, agendaCheckKey, type AgendaCheck } from "./agendaChecks";

/** "12:05" from seconds into the recording. */
function clock(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

export function RecorderAgendaPill({
  checks,
  locked,
  onToggle,
}: {
  checks: AgendaCheck[];
  locked: boolean;
  onToggle: (key: string) => void;
}) {
  const { done, total } = agendaCheckCounts(checks);
  const multiNote = new Set(checks.map((c) => c.noteId)).size > 1;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Agenda, ${done} of ${total} done`}
          // The dock's own glass, the size of the line above it.
          className="dock-glass inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-full px-3 text-[12px] font-semibold text-foreground transition-colors hover:text-brand"
        >
          <AgendaFlag className="h-3.5 w-3.5 text-brand" />
          Agenda
          <span className="tabular-nums text-muted-foreground">
            {done}/{total}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="center"
        sideOffset={12}
        className="max-h-[min(60vh,420px)] w-80 overflow-y-auto p-1.5"
      >
        <DropdownMenuLabel className="flex items-baseline justify-between gap-2 px-2 pb-1.5 pt-1">
          <span className="text-sm font-semibold text-foreground">Agenda</span>
          <span className="text-[11px] font-normal text-muted-foreground">
            {locked ? "Recording stopped" : "Tick what you've covered"}
          </span>
        </DropdownMenuLabel>
        {total === 0 ? (
          <p className="px-2 pb-2 text-[12px] text-muted-foreground">This agenda has no points yet.</p>
        ) : (
          checks.map((c, i) => {
            const heading = multiNote && (i === 0 || checks[i - 1].noteId !== c.noteId);
            return (
              <div key={agendaCheckKey(c)}>
                {heading ? (
                  <p className="truncate px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {c.noteTitle}
                  </p>
                ) : null}
                <DropdownMenuItem
                  // Stays open: several items get ticked in a row.
                  onSelect={(e) => {
                    e.preventDefault();
                    if (!locked) onToggle(agendaCheckKey(c));
                  }}
                  aria-disabled={locked || undefined}
                  role="menuitemcheckbox"
                  aria-checked={c.checked}
                  className={cn(
                    "items-start gap-2.5 rounded-lg px-2 py-2",
                    c.parentText ? "pl-8" : "",
                    locked && "cursor-default",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors",
                      c.checked ? "border-brand bg-brand text-white" : "border-muted-foreground/50",
                    )}
                  >
                    {c.checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                  </span>
                  <span
                    dir={isRTLText(c.text) ? "rtl" : "ltr"}
                    className={cn(
                      "min-w-0 flex-1 text-[13px] leading-snug",
                      c.checked ? "text-muted-foreground" : "text-foreground",
                    )}
                  >
                    {c.text}
                  </span>
                  {c.checked && typeof c.atSecs === "number" ? (
                    <span className="shrink-0 pt-px font-mono text-[11px] tabular-nums text-muted-foreground">
                      {clock(c.atSecs)}
                    </span>
                  ) : null}
                </DropdownMenuItem>
              </div>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
